import { clamp, smoothstep } from "../utils/math.js";

export class TimelineAnalyzer {
  constructor() {
    this.player = null;
    this.video = null;
    this.lastPosition = 0;
    this.currentPhrase = null;
    this.currentWord = null;
    this.currentChar = null;
    this.currentBeat = null;
    this.currentChorus = null;
    this.choruses = [];
    this.duration = 0;
  }

  setSource(player, video) {
    this.player = player;
    this.video = video;
    this.lastPosition = 0;
    this.currentPhrase = null;
    this.currentWord = null;
    this.currentChar = null;
    this.currentBeat = null;
    this.currentChorus = null;
    this.choruses = player?.getChoruses?.() || [];
    this.duration = video?.duration || 0;
  }

  reset(position = 0) {
    this.lastPosition = position;
    this.currentPhrase = null;
    this.currentWord = null;
    this.currentChar = null;
    this.currentBeat = null;
    this.currentChorus = null;
  }

  update(position, metrics = { vocal: 0, valence: 0, arousal: 0 }) {
    if (!this.player || !this.video) {
      return this.#emptySnapshot(position, metrics);
    }

    const now = clamp(position, 0, this.duration || Number.MAX_SAFE_INTEGER);
    const previousPosition = this.lastPosition;
    const movingForward = now >= previousPosition;
    const phrase = this.video.findPhrase(now);
    const word = this.video.findWord(now);
    const char = this.video.findChar(now);
    const beat = this.player.findBeat(now);
    const chorus = this.player.findChorus(now);

    // TextAlive's time-range API is used here instead of guessing from frame
    // deltas. This catches beat/lyric transitions even when a frame is late.
    const beatChange =
      movingForward && previousPosition !== now
        ? this.player.findBeatChange(previousPosition, now)
        : null;
    const phraseChange =
      movingForward && previousPosition !== now
        ? this.video.findPhraseChange(previousPosition, now)
        : null;
    const wordChange =
      movingForward && previousPosition !== now
        ? this.video.findWordChange(previousPosition, now)
        : null;
    const chorusChange =
      movingForward && previousPosition !== now
        ? this.player.findChorusChange(previousPosition, now)
        : null;

    const nextChorus = this.#getNextChorus(now);
    const section = this.#sectionFor(now, chorus, nextChorus);
    const chorusIntensity = this.#chorusIntensity(now, chorus, nextChorus);
    const endingIntensity = smoothstep(Math.max(0, this.duration - 12000), this.duration, now);
    const mood = this.#moodFor(section, now, metrics, chorusIntensity, endingIntensity, nextChorus);
    const phraseProgress = phrase ? phrase.progress(now) : 0;
    const wordProgress = word ? word.progress(now) : 0;
    const beatProgress = beat ? beat.progress(now) : 0;
    const totalProgress = this.duration ? now / this.duration : 0;

    const snapshot = {
      position: now,
      previousPosition,
      duration: this.duration,
      totalProgress,
      phrase,
      word,
      char,
      beat,
      chorus,
      nextChorus,
      section,
      mood,
      phraseProgress,
      wordProgress,
      beatProgress,
      chorusIntensity,
      endingIntensity,
      metrics,
      changes: {
        beat: beatChange,
        phrase: phraseChange,
        word: wordChange,
        chorus: chorusChange,
      },
      entered: {
        phrase: phrase && phrase !== this.currentPhrase,
        word: word && word !== this.currentWord,
        char: char && char !== this.currentChar,
        beat: beat && beat !== this.currentBeat,
        chorus: chorus && chorus !== this.currentChorus,
      },
    };

    this.currentPhrase = phrase;
    this.currentWord = word;
    this.currentChar = char;
    this.currentBeat = beat;
    this.currentChorus = chorus;
    this.lastPosition = now;

    return snapshot;
  }

  #emptySnapshot(position, metrics) {
    return {
      position,
      previousPosition: this.lastPosition,
      duration: 0,
      totalProgress: 0,
      phrase: null,
      word: null,
      char: null,
      beat: null,
      chorus: null,
      nextChorus: null,
      section: "intro",
      mood: this.#moodFor("intro", position, metrics, 0, 0, null),
      phraseProgress: 0,
      wordProgress: 0,
      beatProgress: 0,
      chorusIntensity: 0,
      endingIntensity: 0,
      metrics,
      changes: {},
      entered: {},
    };
  }

  #sectionFor(position, chorus, nextChorus) {
    const firstPhraseStart = this.video?.firstPhrase?.startTime || 0;
    if (position < Math.max(0, firstPhraseStart - 900)) return "intro";
    if (this.duration && position > this.duration - 9000) return "ending";
    if (chorus) return "chorus";
    if (nextChorus && nextChorus.startTime - position < 11000) return "pre-chorus";
    return "verse";
  }

  #getNextChorus(position) {
    return this.choruses.find((chorus) => chorus.startTime > position) || null;
  }

  #chorusIntensity(position, chorus, nextChorus) {
    if (chorus) {
      const attack = smoothstep(chorus.startTime, chorus.startTime + 1800, position);
      const release = 1 - smoothstep(chorus.endTime - 2600, chorus.endTime, position);
      return clamp(Math.min(attack, release) + 0.16, 0, 1);
    }

    if (!nextChorus) return 0;
    return smoothstep(nextChorus.startTime - 9000, nextChorus.startTime, position) * 0.72;
  }

  #moodFor(section, position, metrics, chorusIntensity, endingIntensity, nextChorus) {
    const vocal = metrics?.vocal || 0;
    const arousal = Math.max(0, metrics?.arousal || 0);
    const valence = metrics?.valence || 0;
    const preChorusBuild =
      section === "pre-chorus" && nextChorus
        ? smoothstep(nextChorus.startTime - 11000, nextChorus.startTime, position)
        : 0;

    const sectionBase = {
      intro: { energy: 0.2, tension: 0.16, intimacy: 0.72, release: 0.08 },
      verse: { energy: 0.32, tension: 0.22, intimacy: 0.82, release: 0.12 },
      "pre-chorus": { energy: 0.54, tension: 0.78, intimacy: 0.5, release: 0.18 },
      chorus: { energy: 0.94, tension: 0.42, intimacy: 0.34, release: 1 },
      ending: { energy: 0.22, tension: 0.1, intimacy: 0.9, release: 0.32 },
    }[section] || { energy: 0.35, tension: 0.25, intimacy: 0.65, release: 0.16 };

    const energy = clamp(
      sectionBase.energy + vocal * 0.22 + arousal * 0.12 + chorusIntensity * 0.22 + preChorusBuild * 0.16 - endingIntensity * 0.18,
    );
    const tension = clamp(sectionBase.tension + preChorusBuild * 0.28 + arousal * 0.18 - chorusIntensity * 0.18);
    const intimacy = clamp(sectionBase.intimacy + (1 - energy) * 0.15 - chorusIntensity * 0.2 + endingIntensity * 0.22);
    const release = clamp(sectionBase.release + chorusIntensity * 0.72 + Math.max(0, valence) * 0.12 - endingIntensity * 0.12);
    const motion = clamp(energy * 0.68 + tension * 0.22 + chorusIntensity * 0.28);

    return {
      energy,
      tension,
      intimacy,
      release,
      motion,
      build: preChorusBuild,
      warmth: clamp((valence + 1) * 0.5),
    };
  }
}
